<template>
	<div>
		<div class="p-text-left">
			<h2 class="titleFont p-text-bold p-mb-2">Step 3: Payment Information</h2>
			<p>Please fill out your information below.</p>
		</div>
		
		<Message
			v-show="store.formSubmissionData.errorMessage != null"
			severity="error"
		>
			{{ store.formSubmissionData.errorMessage }}
		</Message>

		<div class="p-d-flex p-jc-center">
			<span class="p-buttonset p-col-6 p-d-flex p-flex-column p-flex-md-row" >
				<Button 
				:class="{ 'p-button-secondary' : paymentMethodSB === 'CCHQ' }" 
				label="Credit Card" 
				icon="pi pi-credit-card" 
				@click="handleCLickCC()" 
				style="border-radius: 6px;"/>

				<Button 
				:class="{ 'p-button-secondary': paymentMethodSB === 'CARD' }" 
				label="Invoice" 
				icon="pi pi-dollar" 
				@click="handleCLickCHQ()" 
				style="border-radius: 6px;"/>
			</span>
		</div>
		<div class="p-col-12 p-d-flex p-jc-center">
			<div v-show="paymentMethodSB == 'CARD'" class="p-fluid p-grid p-formgrid p-text-left p-jc-center p-mt-4 p-col-8">
				<div class="p-field p-col-12 p-sm-6">
					<label for="cardNumber" class="p-text-bold">Card Number *</label>
					<div class="p-inputgroup">
						<InputText
							id="cardNumber"
							v-model="cardNumber"
							placeholder="Card Number"
							v-mask="
								cardType == 'AMEX' ? '#### ###### #####' : '#### #### #### ####'
							"
							inputmode="numeric"
						/>

						<span
							id="cardNumber"
							class="p-inputgroup-addon p-py-0"
							style="background: transparent"
						>
							<CardVisa v-if="cardType == 'Visa'" />
							<CardMastercard v-else-if="cardType == 'Mastercard'" />
							<CardDiscover v-else-if="cardType == 'Discover'" />
							<CardAmericanExpress v-else-if="cardType == 'AMEX'" />
						</span>
					</div>

					<span v-if="v$.cardNumber.$error && submitted">
						<span
							id="cardNumber-error"
							v-for="(error, index) of v$.cardNumber.$errors"
							:key="index"
						>
							<small class="p-error p-text-bold">
								{{
									error.$message == "Value is required"
									? error.$message.replace("Value", "Card Number")
									: cardType == "AMEX"
										? error.$message.replace(
											"This field should be at least 17 characters long",
											"Invalid card number"
										)
										: error.$message.replace(
											"This field should be at least 19 characters long",
											"Invalid card number"
										)
								}}
							</small>
						</span>
					</span>
				</div>

				<div class="p-field p-col-12 p-sm-6 p-text-left">
					<label for="cardholderName" class="p-text-bold">
						Cardholder Name *
					</label>

					<InputText
						id="cardholderName"
						v-model="cardholderName"
						placeholder="Cardholder Name"
						type="text"
					/>

					<small
						v-if="v$.cardholderName.$invalid && submitted"
						class="p-error p-text-bold"
					>
						{{
							v$.cardholderName.required.$message.replace(
								"Value",
								"Cardholder Name"
							)
						}}
					</small>
				</div>

				<div class="p-field p-mb-3 p-col-8 p-text-left">
					<label for="cardExpiryDate" class="p-text-bold">
						Expiry Date *
					</label>

					<InputMask
						id="cardExpiryDate"
						v-model="cardExpiry"
						placeholder="MM/YY"
						mask="99/99"
						slotChar=""
						inputmode="numeric"
					/>

					<span v-if="v$.cardExpiry.$error && submitted">
						<span
							id="cardExpiry-error"
							v-for="(error, index) of v$.cardExpiry.$errors"
							:key="index"
						>
							<small class="p-error p-text-bold">
								{{
									error.$message == "Value is required"
									? error.$message.replace("Value", "Expiry Date")
									: error.$message.replace(
										"This field should be at least 5 characters long",
										"Invalid expiry date"
									)
								}}
							</small>
						</span>
					</span>
				</div>

				<div class="p-field p-mb-3 p-col-4 p-text-left">
					<label for="cvv" class="p-text-bold">CVV *</label>

					<InputText
						id="cvv"
						v-model="cvv"
						placeholder="CVV"
						v-mask="'####'"
						inputmode="numeric"
					/>

					<small
						v-if="v$.cvv.$invalid && submitted"
						class="p-error p-text-bold"
					>
						{{ v$.cvv.required.$message.replace("Value", "CVV") }}
					</small>
				</div>
			</div>
		</div>
		

		<div class="p-d-flex p-mt-3 p-no-gutters">
			<div class="p-mb-3 p-col-6 p-pl-0">
				<Button
					label="Back"
					icon="pi pi-angle-left"
					class="p-button-secondary"
					style="min-width: 120px; max-width: 120px"
					@click="prevStep"
				/>
			</div>

			<div class="p-mb-3 p-col-6 p-pr-0">
				<Button
					label="Next"
					icon="pi pi-angle-right"
					iconPos="right"
					style="min-width: 120px; max-width: 120px"
					@click="nextStep"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import CardVisa from "../../icons/creditCards/CardVisa.vue";
import CardMastercard from "../../icons/creditCards/CardMastercard.vue";
import CardDiscover from "../../icons/creditCards/CardDiscover.vue";
import CardAmericanExpress from "../../icons/creditCards/CardAmericanExpress.vue";
import { useFormDataStore } from "../../store/formData.js";

import { useVuelidate } from "@vuelidate/core";
import { required, minLength } from "@vuelidate/validators";

export default {
	name: "S3Form",
	data() {
		return {
			paymentMethodSB: 'CARD',
			cardholderName: null,
			cardNumber: null,
			cardExpiry: null,
			cvv: null,
			cardType: null,
			submitted: false,
		};
	},
	computed: {
		canadaProvinceOptions() {
			return this.store.provinceOptions;
		},
	},
	components: {
		CardVisa,
		CardMastercard,
		CardDiscover,
		CardAmericanExpress,
	},
	setup() {
		const store = useFormDataStore();

		return {
			store,
			v$: useVuelidate(),
		};
	},
	validations() {
		// if (this.paymentMethodSB == "CARD"){
			return {
				cardholderName: this.paymentMethodSB === "CARD" ? { required } : {},
				cardNumber: this.paymentMethodSB === "CARD" ? {
					required,
					minLength: this.cardType == "AMEX" ? minLength(17) : minLength(19),
				} : {},
				cardExpiry: this.paymentMethodSB === "CARD" ? { required, minLength: minLength(5) } : {},
				cvv: this.paymentMethodSB === "CARD" ? { required } : {},
			};
		// }
	},
	watch: {
		cardNumber(input) {
			if (input != null) {
				if (input.substring(0, 1) == "4") {
					this.cardType = "Visa";
				} else if (input.substring(0, 1) == "5") {
					this.cardType = "Mastercard";
				} else if (input.substring(0, 1) == "6") {
					this.cardType = "Discover";
				} else if (
					input.substring(0, 2) == "34" ||
					input.substring(0, 2) == "37"
				) {
					this.cardType = "AMEX";
				} else {
					this.cardType = null;
				}
			}
		},
	},
	methods: {
		searchProvinceState() {
			this.filteredProvinceState = [];
		},
		handleCLickCC(){
			this.paymentMethodSB = 'CARD';
		},
		handleCLickCHQ() {
			this.paymentMethodSB = 'CCHQ';

		},
		
		async nextStep() {
			if (this.paymentMethodSB === "CARD") {
				const isFormValid = await this.v$.$validate();
				this.submitted = true;

				if (isFormValid) {
					this.store.setS3FormData({
						cardHolder: this.cardholderName.trim(),
						cardNumber: this.cardNumber.split(" ").join(""),
						expiryMonth: this.cardExpiry.split("/")[0],
						expiryYear: "20" + this.cardExpiry.split("/")[1],
						cvv: this.cvv,
						paymentMethodSB: this.paymentMethodSB

					});

					this.$emit("nextStep");
				} else {
					this.scrollToFirstError();

					return;
				}
			} else {
				this.store.setS3FormData({
					paymentMethodSB: this.paymentMethodSB
				});
				this.$emit("nextStep");

			}
			
		},
		prevStep() {
			this.$emit("prevStep");
		},
		scrollToFirstError() {
			this.$nextTick(() => {
				if (document.querySelector(".p-error p-text-bold")) {
					let domRect = document
						.querySelector(".p-error p-text-bold")
						.getBoundingClientRect();

					window.scrollTo({
						left: domRect.left + document.documentElement.scrollLeft,
						top: domRect.top + document.documentElement.scrollTop - 100,
						behavior: "smooth",
					});
				}
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.titleFont {
	font-size: 1.9rem;
}

.p-button {
	border: white 2px solid;
}

</style>
